import {
  Button,
  Code,
  Flex,
  Text,
  Tooltip,
  FormControl,
  FormLabel,
  Grid,
  Select as ChakraSelect,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useDisclosure,
  Input as ChakraInput,
} from "@chakra-ui/react";
import { Select as ChakraReactSelect } from "chakra-react-select";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useFormik } from "formik";
import { MainModal } from "@/components/MainModal/MainModal";
import { Table } from "@/components/Table/Table";
import { Pagination } from "@/components/Pagination/Pagination";
import { useSorting } from "@/hooks/useSorting";
import { formatDate } from "@/helpers/formatDate";
import { skillsSearchingFn } from "@/helpers/SkillsSortingFn";
import { CellContext, ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useAppDispatch, useAppSelector } from "@/store";
import { RootState } from "@/store";
import { fetchAllCandidates } from "@/store/slices/candidates";
import { fetchVacancyApplicationCreate } from "@/store/slices/vacancyApplications";
import { fetchSingleVacancy } from "@/store/slices/vacancy";
import { ISingleCandidate } from "@/api/candidate/types";
import { StatusCell } from "@/components/StatusCell/StatusCell";
import { FiFilter } from "react-icons/fi";
import Select from "react-select";
import api from "@/api";
import { fetchAllDepartments } from "@/store/slices/departments";
import { transformGuides } from "@/helpers/transformGuides";

interface AddCandidateToVacancyModalProps {
  isOpen: boolean;
  vacancyId: number;
  onClose: () => void;
}

interface ISingleCandidateRow {
  id: number;
  last_name: string;
  first_name: string;
  patronymic_name?: string;
  expected_position: string;
  contact_telegram?: string;
  created_at: string | number;
  created_by: { username: string };
  status: { name: string };
  skills?: { id: number; name: string }[];
}

const colourStyles = {
  control: (styles: any) => ({
    ...styles,
    width: "300px",
    maxWidth: "473px",
    borderColor: "#E2E8F0",
    height: "40px",
    zIndex: 1,
  }),
  menu: (styles: any) => ({
    ...styles,
    zIndex: 5000,
  }),
};

const columnHelper = createColumnHelper<ISingleCandidate>();

export const AddCandidateToVacancyModal = ({ isOpen, vacancyId, onClose }: AddCandidateToVacancyModalProps) => {
  const SkillsCell = (info: CellContext<ISingleCandidate, string>) => {
    const skills = info?.row.original.skills;
    const skillNames = useMemo(() => skills?.map((skill) => skill.name) || [], [skills]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(skillNames.length);

    useEffect(() => {
      if (!containerRef.current || skillNames.length === 0) {
        setVisibleCount(skillNames.length);
        return;
      }

      const containerWidth = containerRef.current.offsetWidth - 8; // Учитываем padding

      if (!containerWidth) {
        setVisibleCount(skillNames.length);
        return;
      }

      // Примерная ширина элемента "+N" (около 40px)
      const plusElementWidth = 40;
      const gap = 4; // gap между элементами

      let totalWidth = 0;
      let count = 0;

      // Создаем временный элемент для измерения ширины каждого навыка
      const measureElement = document.createElement("span");
      measureElement.style.fontSize = "12px";
      measureElement.style.fontWeight = "600";
      measureElement.style.padding = "2px 10px";
      measureElement.style.border = "1px solid #e1e7ef";
      measureElement.style.borderRadius = "9999px";
      measureElement.style.visibility = "hidden";
      measureElement.style.position = "absolute";
      measureElement.style.whiteSpace = "nowrap";
      document.body.appendChild(measureElement);

      for (let i = 0; i < skillNames.length; i++) {
        measureElement.textContent = skillNames[i];
        const skillWidth = measureElement.offsetWidth;

        const willNeedPlusElement = i < skillNames.length - 1;
        const requiredWidth = totalWidth + skillWidth + (willNeedPlusElement ? gap + plusElementWidth : 0);

        if (requiredWidth <= containerWidth) {
          totalWidth += skillWidth + (i > 0 ? gap : 0);
          count++;
        } else {
          break;
        }
      }

      document.body.removeChild(measureElement);

      // Если не все навыки помещаются, уменьшаем количество на 1 для места под "+N"
      if (count < skillNames.length && count > 0) {
        count = Math.max(1, count - 1);
      }

      setVisibleCount(count);
    }, [skillNames]);

    const visibleSkills = skillNames.slice(0, visibleCount);
    const hiddenSkills = skillNames.slice(visibleCount);
    const hasHiddenSkills = hiddenSkills.length > 0;

    return (
      <Flex ref={containerRef} alignItems="center" gap="2px" flexWrap="nowrap" width="100%" overflow="hidden" minHeight="20px" maxWidth="15vw">
        {visibleSkills.map((skill) => (
          <Text
            key={skill}
            fontSize={12}
            fontWeight={600}
            lineHeight="16px"
            padding="2px 8px"
            borderRadius="9999px"
            border="1px solid #e1e7ef"
            whiteSpace="nowrap"
            flexShrink={0}
          >
            {skill}
          </Text>
        ))}
        {hasHiddenSkills && (
          <Tooltip
            label={
              <Flex flexDirection="column" gap="2px">
                {hiddenSkills.map((skill) => (
                  <Text key={skill} fontSize={12}>
                    {skill}
                  </Text>
                ))}
              </Flex>
            }
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            borderRadius="6px"
            p={2}
          >
            <Text
              fontSize={12}
              fontWeight={600}
              lineHeight="16px"
              padding="2px 10px"
              borderRadius="9999px"
              border="1px solid #e1e7ef"
              bg="#f7fafc"
              color="#4a5568"
              cursor="pointer"
              whiteSpace="nowrap"
              flexShrink={0}
              _hover={{
                bg: "#edf2f7",
                borderColor: "#cbd5e0",
              }}
            >
              +{hiddenSkills.length}
            </Text>
          </Tooltip>
        )}
      </Flex>
    );
  };
  const dispatch = useAppDispatch();
  const { candidates } = useAppSelector((state: RootState) => state.candidates);
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);

  const { sortColumn, sortDirection, handleSort } = useSorting("candidate.created_at");

  // Фильтры
  const [channels, setChannels] = useState<{ value: number; label: string }[]>([{ value: 0, label: "Все каналы" }]);
  const [skillList, setSkillList] = useState<{ value: string; label: string }[]>([]);
  const [sourceList, setSourceList] = useState<{ value: string; label: string }[]>([]);
  const [statusList, setStatusList] = useState<{ value: string; label: string }[]>([]);
  const [recruiters, setRecruiters] = useState<{ value: number; label: string }[]>([]);

  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();

  // Состояния для оптимизации селекта навыков
  const [skillSearchInput, setSkillSearchInput] = useState("");
  const [skillSearchDebounced, setSkillSearchDebounced] = useState("");

  // Активные фильтры
  const [channelId, setChannelId] = useState<number>(0);
  const [departmentId, setDepartmentId] = useState<number | undefined>(undefined);
  const [draftDepartmentId, setDraftDepartmentId] = useState<number | undefined>(undefined);
  const [skill, setSkill] = useState<string[]>([]);
  const [draftSkill, setDraftSkill] = useState<{ value: string; label: string }[]>([]);
  const [sourceId, setSourceId] = useState<number | undefined>(undefined);
  const [draftSourceId, setDraftSourceId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [draftStatus, setDraftStatus] = useState<string | undefined>(undefined);
  const [createdById, setCreatedById] = useState<number | undefined>(undefined);
  const [draftCreatedById, setDraftCreatedById] = useState<number | undefined>(undefined);
  const [createdAtFrom, setCreatedAtFrom] = useState<string | undefined>(undefined);
  const [draftCreatedAtFrom, setDraftCreatedAtFrom] = useState<string | undefined>(undefined);
  const [createdAtTo, setCreatedAtTo] = useState<string | undefined>(undefined);
  const [draftCreatedAtTo, setDraftCreatedAtTo] = useState<string | undefined>(undefined);

  const formik = useFormik<{ vacancy_id: number; candidate_id: number }>({
    initialValues: { vacancy_id: vacancyId, candidate_id: 0 },
    onSubmit: () => {
      if (!vacancyId || formik.values.candidate_id === 0) {
        return;
      }
      dispatch(fetchVacancyApplicationCreate({ vacancy_id: vacancyId, candidate_id: formik.values.candidate_id } as any)).then(() => {
        dispatch(fetchSingleVacancy({ params: { id: Number(vacancyId) } }));
        onClose();
      });
    },
    enableReinitialize: true,
  });

  const handleRowClick = (row: Row<ISingleCandidateRow>) => {
    row.toggleSelected();
    if (!row.getIsSelected()) {
      formik.setFieldValue("candidate_id", row.original.id);
    } else {
      formik.setFieldValue("candidate_id", 0);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleOpenFilters = () => {
    setDraftCreatedById(createdById);
    setDraftCreatedAtFrom(createdAtFrom);
    setDraftCreatedAtTo(createdAtTo);
    setDraftStatus(status);
    setDraftSourceId(sourceId);
    setDraftDepartmentId(departmentId);
    setDraftSkill(skillList.filter((el) => skill.includes(el.value)));
    onFiltersOpen();
  };

  const handleResetFilters = () => {
    setDraftDepartmentId(undefined);
    setDraftSkill([]);
    setDraftSourceId(undefined);
    setDraftStatus(undefined);
    setDraftCreatedById(undefined);
    setDraftCreatedAtTo(undefined);
    setDraftCreatedAtFrom(undefined);
  };

  const handleApplyFilters = () => {
    setDepartmentId(draftDepartmentId);
    setSkill(draftSkill.map((skill) => skill.value));
    setSourceId(draftSourceId);
    setStatus(draftStatus);
    setCreatedById(draftCreatedById);
    setCreatedAtFrom(draftCreatedAtFrom);
    setCreatedAtTo(draftCreatedAtTo);
    setPage(1);
    onFiltersClose();
  };

  // Колбэк для обработки изменения поискового запроса
  const handleSkillInputChange = useCallback((inputValue: string) => {
    setSkillSearchInput(inputValue);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const offset = (page - 1) * limit;
    dispatch(
      fetchAllCandidates({
        limit,
        offset,
        search,
        sort_column: sortColumn || undefined,
        sort_direction: sortDirection || undefined,
        channel_id: !channelId || channelId === 0 ? undefined : channelId,
        department_id: !departmentId || departmentId === 0 ? undefined : departmentId,
        skill: skill.length > 0 ? skill : undefined,
        source_id: !sourceId || sourceId === 0 ? undefined : sourceId,
        status: !status || status === "" ? undefined : status,
        created_by: !createdById || createdById === 0 ? undefined : createdById,
        created_at_from: createdAtFrom ? String(Math.floor(new Date(createdAtFrom).getTime() / 1000)) : undefined,
        created_at_to: createdAtTo ? String(Math.floor(new Date(createdAtTo).getTime() / 1000)) : undefined,
      } as any),
    ).then((res: any) => {
      if (res && res.payload && typeof res.payload.count === "number") {
        setTotalItems(res.payload.count);
      }
    });
  }, [
    dispatch,
    isOpen,
    page,
    limit,
    search,
    sortColumn,
    sortDirection,
    channelId,
    departmentId,
    skill,
    sourceId,
    status,
    createdById,
    createdAtFrom,
    createdAtTo,
  ]);

  // Загрузка каналов и рекрутеров
  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      const { data } = await api.channel.getChannels();
      const channels = data.map((channel) => ({
        value: channel.id,
        label: channel.name,
      })) as { value: number; label: string }[];
      channels.unshift({ value: 0, label: "Все каналы" });
      setChannels(channels);
      const recruiters = await api.recruiters.recruiters({});
      setRecruiters(
        recruiters.data.map((recruiter) => ({
          value: recruiter.id,
          label: recruiter.username,
        })),
      );
    })();
  }, [isOpen]);

  // Загрузка департаментов при изменении канала
  useEffect(() => {
    if (!isOpen) return;

    if (!!channelId) {
      dispatch(fetchAllDepartments(channelId));
    } else {
      dispatch(fetchAllDepartments());
    }
  }, [channelId, dispatch, isOpen]);

  // Обработка гайдов
  useEffect(() => {
    setSkillList(transformGuides(guides?.Skill) || []);
    setSourceList(transformGuides(guides?.CandidateSource) || []);
    setStatusList(transformGuides(guides?.CandidateStatus) || []);
  }, [guides]);

  // Дебаунс для поиска по навыкам
  useEffect(() => {
    const timer = setTimeout(() => {
      setSkillSearchDebounced(skillSearchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [skillSearchInput]);

  // Мемоизированная фильтрация навыков для лучшей производительности
  const filteredSkillOptions = useMemo(() => {
    if (!skillSearchDebounced.trim()) {
      // Если нет поискового запроса, показываем только первые 50 опций
      return skillList.slice(0, 50);
    }

    const searchTerm = skillSearchDebounced.toLowerCase();
    const filtered = skillList.filter((skill) => skill.label.toLowerCase().includes(searchTerm) || skill.value.toLowerCase().includes(searchTerm));

    // Ограничиваем количество результатов для производительности
    return filtered.slice(0, 100);
  }, [skillList, skillSearchDebounced]);

  const columns = useMemo<Array<ColumnDef<ISingleCandidate, any>>>(
    () => [
      columnHelper.accessor("last_name", {
        id: "name",
        header: "ФИО",
        cell: (info) => (
          <Tooltip
            label={`${info.row.original.last_name ?? ""} ${info.row.original.first_name ?? ""} ${info.row.original.patronymic_name ?? ""}`}
            hasArrow
          >
            <Text
              maxW="120px"
              noOfLines={1}
              fontSize={14}
              fontWeight={600}
            >{`${info.row.original.last_name ?? ""} ${info.row.original.first_name ?? ""} ${info.row.original.patronymic_name ?? ""}`}</Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("expected_position", {
        id: "expected_position",
        header: "Должность",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("skills", {
        id: "skills",
        header: () => (
          <Text w="100%" maxWidth="14vw">
            Ключевые навыки
          </Text>
        ),
        cell: (info) => SkillsCell(info),
        enableColumnFilter: true,
        filterFn: skillsSearchingFn,
        meta: {
          type: "array",
        },
        size: 180,
        minSize: 100,
      }),
      columnHelper.accessor("contact_telegram", {
        id: "contact_telegram",
        header: "Телеграмм",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Code background="none" color="#65758b" whiteSpace="wrap" wordBreak="break-word">
              {info.getValue()}
            </Code>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 140,
        minSize: 100,
      }),
      columnHelper.accessor("source.name", {
        id: "source_name",
        header: "Источник",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="80px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 80,
        minSize: 40,
      }),
      columnHelper.accessor("status.name", {
        id: "status_name",
        header: "Статус кандидата",
        cell: (info) => StatusCell(info.getValue()),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("created_by.username", {
        header: "Кем создано",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="120px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("created_at", {
        header: "Дата создания",
        cell: (info) => (
          <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
            <Text noOfLines={1} maxW="100px">
              {formatDate(info.getValue() || "")}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        size: 100,
        minSize: 80,
      }),
    ],
    [],
  );

  return (
    <MainModal width={"80vw"} showFooter={false} isOpen={isOpen} onClose={onClose} title={"Добавить кандидата"}>
      <Flex flexDirection="column" gap="16px">
        <Table
          minHeight="0px"
          handleSearch={handleSearch}
          list={candidates}
          columns={columns}
          addClick={() => {}}
          withoutNavigate={true}
          maxHeight="50vh"
          canSelect={true}
          rowClick={handleRowClick}
          sortHandler={handleSort}
          sortState={{ column: sortColumn || "", direction: sortDirection }}
          leftExtra={
            <Flex gap="8px" alignItems="center">
              <Select
                menuPosition="absolute"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                isClearable={channelId !== 0}
                name="color"
                styles={colourStyles}
                options={channels}
                value={channels.find((channel) => channel.value === channelId) || null}
                placeholder="Выберите канал"
                onChange={(value) => {
                  setChannelId(value?.value || 0);
                  setDraftDepartmentId(undefined);
                  setDepartmentId(undefined);
                }}
              />
              <Popover placement="bottom-start" closeOnBlur isLazy strategy="fixed" isOpen={isFiltersOpen} onClose={onFiltersClose}>
                <PopoverTrigger>
                  <Button
                    leftIcon={<Icon as={FiFilter as any} boxSize={4} />}
                    onClick={handleOpenFilters}
                    bg="#2563eb"
                    color="white"
                    _hover={{ bg: "#2563eb" }}
                    _active={{ bg: "#2563eb" }}
                  >
                    Фильтры
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent w="700px" maxW="700px" minW="700px" overflow="visible" _focus={{ boxShadow: "lg" }}>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader fontWeight={600}>Фильтры</PopoverHeader>
                    <PopoverBody maxH="calc(100vh - 300px)" overflowY="auto">
                      <Flex direction="column" gap="16px">
                        {/* Ключевые навыки занимают всю ширину */}
                        <FormControl>
                          <FormLabel>Ключевые навыки</FormLabel>
                          <ChakraReactSelect
                            isMulti
                            placeholder="Ключевые навыки (начните вводить для поиска)"
                            value={draftSkill}
                            onChange={(selectedOptions) => {
                              setDraftSkill(selectedOptions ? [...selectedOptions] : []);
                            }}
                            options={filteredSkillOptions}
                            isSearchable
                            isClearable
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            onInputChange={handleSkillInputChange}
                            inputValue={skillSearchInput}
                            filterOption={null}
                            maxMenuHeight={320}
                            menuPlacement="auto"
                            chakraStyles={{
                              container: (provided) => ({
                                ...provided,
                                minHeight: "40px",
                              }),
                              control: (provided, state) => ({
                                ...provided,
                                borderColor: state.isFocused ? "#3182ce" : "#E2E8F0",
                                boxShadow: state.isFocused ? "0 0 0 1px #3182ce" : "none",
                                "&:hover": {
                                  borderColor: state.isFocused ? "#3182ce" : "#CBD5E0",
                                },
                              }),
                              multiValue: (provided) => ({
                                ...provided,
                                backgroundColor: "#EDF2F7",
                                borderRadius: "6px",
                              }),
                              multiValueLabel: (provided) => ({
                                ...provided,
                                color: "#2D3748",
                                fontSize: "14px",
                              }),
                              multiValueRemove: (provided) => ({
                                ...provided,
                                color: "#718096",
                                "&:hover": {
                                  backgroundColor: "#E53E3E",
                                  color: "white",
                                },
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                color: "#A0AEC0",
                                fontSize: "14px",
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              menuList: (provided) => ({
                                ...provided,
                                maxHeight: "320px",
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                fontSize: "14px",
                                padding: "8px 12px",
                              }),
                            }}
                            noOptionsMessage={({ inputValue }) => {
                              if (!inputValue.trim()) {
                                return "Введите текст для поиска навыков";
                              }
                              return `Навыки "${inputValue}" не найдены`;
                            }}
                          />
                        </FormControl>

                        {/* Основные фильтры в 2 колонки */}
                        <Grid templateColumns="repeat(2, 1fr)" gap="16px">
                          <FormControl>
                            <FormLabel>Источник</FormLabel>
                            <ChakraSelect
                              placeholder="Выберите источник"
                              value={draftSourceId || ""}
                              onChange={(e) => {
                                setDraftSourceId(Number(e.target.value));
                              }}
                            >
                              {sourceList.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </ChakraSelect>
                          </FormControl>

                          <FormControl>
                            <FormLabel>Статус кандидата</FormLabel>
                            <ChakraSelect
                              placeholder="Выберите статус"
                              value={draftStatus || ""}
                              onChange={(e) => {
                                setDraftStatus(e.target.value);
                              }}
                            >
                              {statusList.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </ChakraSelect>
                          </FormControl>
                        </Grid>
                        <FormControl>
                          <FormLabel>Создан кем</FormLabel>
                          <ChakraSelect
                            placeholder="Выберите пользователя"
                            value={draftCreatedById || ""}
                            onChange={(e) => {
                              setDraftCreatedById(Number(e.target.value));
                            }}
                          >
                            {recruiters.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </ChakraSelect>
                        </FormControl>
                        {/* Даты в 2 колонки */}
                        <Grid templateColumns="repeat(2, 1fr)" gap="16px">
                          <FormControl>
                            <FormLabel>Дата с</FormLabel>
                            <ChakraInput
                              type="date"
                              value={draftCreatedAtFrom || ""}
                              onChange={(e) => {
                                setDraftCreatedAtFrom(e.target.value);
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Дата по</FormLabel>
                            <ChakraInput
                              type="date"
                              value={draftCreatedAtTo || ""}
                              onChange={(e) => {
                                setDraftCreatedAtTo(e.target.value);
                              }}
                            />
                          </FormControl>
                        </Grid>

                        <Flex justifyContent="flex-end" gap="8px">
                          <Button variant="ghost" onClick={handleResetFilters}>
                            Сбросить
                          </Button>
                          <Button onClick={handleApplyFilters} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
                            Применить
                          </Button>
                        </Flex>
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </Flex>
          }
        />
        <Pagination
          totalItems={totalItems}
          useUrlParams={false}
          defaultPage={page}
          defaultLimit={limit}
          onPageChange={(offset, lmt) => {
            const newPage = Math.floor(offset / (lmt || limit)) + 1;
            setPage(newPage);
            setLimit(lmt || limit);
          }}
        />
        <Button isDisabled={formik.values.candidate_id === 0} onClick={() => formik.handleSubmit()}>
          Добавить
        </Button>
      </Flex>
    </MainModal>
  );
};
